<template>
	<div class="event-chest-opened" :class="`event-chest-opened--${chestType}`">
		<div class="event-chest-opened__header">
			<div
				class="event-chest-opened__header-bg"
				:class="[`event-chest-opened__header-bg--${chestType}`]"
			>
				<div
					:class="[
						`market-chest-header-bg`,
						`market-chest-header-bg-${chestType}-410`,
					]"
				/>
			</div>

			<div :class="`market-chest-header-heading`">
				<div class="heading-wrapper">
					<ShrinkText
						:text="
							`${multiplier}X ` +
							translate(`event.${chestType}_chest`)
						"
						:fontSize="20"
						:textClassList="`market-chest-header-ribbon-text`"
					/>
				</div>
				<div
					:class="[
						`market-chest-header-ribbon`,
						`market-chest-header-ribbon-${chestType}-410`,
					]"
				/>
			</div>
		</div>
		<div class="event-chest-opened__content">
			<div
				class="event-chest-opened-rewards more-row"
				v-if="mutatedChests.length > 1"
			>
				<div
					class="event-chest-opened-rewards-chest"
					v-for="(chest, index) in mutatedChests"
					:key="index"
				>
					<div class="event-chest-opened-rewards-title">
						{{
							translate("event.chest_number").replace(
								"{number}",
								`${index + 1}`,
							)
						}}
					</div>
					<div class="event-chest-opened-rewards-items">
						<div
							v-for="(item, index) in chest"
							:key="`chest-${index}`"
							:class="`market-chest-reward-bg-${item.type}-85`"
						>
							<Tooltip
								v-if="item.reward?.type === 'currency'"
								:placement="Placements.TOP"
							>
								<template #element>
									<AppPrice
										:amount="item.reward?.amount"
										:currency="item.reward?.currency"
										:resourceCheck="false"
										:iconSize="16"
										:fontSize="14"
									/>
								</template>
								<template #tooltip>
									<span
										v-if="
											item.reward.currency ===
											ResourceType.CHEST_KEY
										"
										>{{
											translate(
												"event.legendary_chest_key",
											)
										}}</span
									>
									<span v-else>
										{{
											translate(
												`resource.${
													item.reward.currency
												}`,
											)
										}}
									</span>
								</template>
							</Tooltip>
							<Tooltip
								v-else
								customWidth="18rem"
								:placement="Placements.TOP"
							>
								<template #element>
									<AppItemAmount
										:amount="item.reward?.amount ?? 0"
										:frameName="item.reward?.frameName"
										:iconSize="16"
										:fontSize="14"
									/>
								</template>
								<template #tooltip>
									<span
										v-html="buildingTooltip(item.reward)"
									/>
								</template>
							</Tooltip>
						</div>
					</div>
				</div>
			</div>
			<div class="event-chest-opened-rewards one-row" v-else>
				<div
					class="event-chest-opened-reward"
					v-for="(chest, index) in mutatedChests[0]"
					:key="index"
				>
					<div class="event-chest-opened-reward-icon">
						<Tooltip
							v-if="chest.reward?.type === 'currency'"
							customWidth="18rem"
							:placement="Placements.TOP"
						>
							<template #element>
								<AppMainIcon
									:iconName="chest.reward.currency"
									:iconSize="72"
								/>
							</template>
							<template #tooltip>
								<span
									v-if="
										chest.reward.currency ===
										ResourceType.CHEST_KEY
									"
									>{{
										translate("event.legendary_chest_key")
									}}</span
								>
								<span v-else>
									{{
										translate(
											`resource.${chest.reward.currency}`,
										)
									}}
								</span>
							</template>
						</Tooltip>
						<Tooltip
							v-else-if="chest.reward?.frameName"
							customWidth="18rem"
							:placement="Placements.TOP"
						>
							<template #element>
								<div
									:class="`icon-${getTileIconName(chest.reward?.frameName)}-${64}`"
								/>
							</template>
							<template #tooltip>
								<span v-html="buildingTooltip(chest.reward)" />
							</template>
						</Tooltip>
					</div>
					<div
						class="event-chest-opened-reward-amount"
						:class="`market-chest-reward-bg-${chest.type}-105`"
						v-if="chest.reward?.amount"
						v-html="
							`${formatNumber(chest.reward?.amount, true)}${chest.reward?.type === 'item' ? 'X' : ''}`
						"
					/>
				</div>
			</div>
			<div class="event-chest-opened-footer">
				<AppTextButton
					class="event-chest-opened__claim"
					:type="StatusTypes.SUCCESS"
					:fontSize="17"
					:text="translate('button.claim').toUpperCase()"
					@click="claimRewards"
				/>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import ShrinkText from "@/components/ShrinkText.vue";
import type {
	GrandPrizeInGameReward,
	GrandPrizeInGameRewardItem,
} from "@/event/index.ts";
import {
	applyMilestoneBuilding,
	applyMilestoneCurrency,
	saveGrandPrizeState,
	toGrandPrizeInGameReward,
	useEventStore,
} from "@/event/index.ts";
import { FrameName } from "@/game/entities/config/index.ts";
import { ResourceType } from "@/game/entities/config/resources.ts";
import { TileToastType, ToastPlacement } from "@/game/ToastMessage.ts";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter.ts";
import useResourcesStore from "@/store/useResourcesStore";
import useUserStore from "@/store/useUserStore.ts";
import type { MarketDropRateType } from "@/types/event.ts";
import type { EventGrandPrizeTierConfig } from "@/types/index.ts";
import { Placements, StatusTypes } from "@/types/ui";
import { formatNumber } from "@/utils/format.ts";
import { floorNumber } from "@/utils/number.ts";
import { replacePlaceholder } from "@/utils/replacePlaceholder.ts";
import { getTileIconName } from "@/utils/tileIconName.ts";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed } from "vue";
import AppItemAmount from "../AppItemAmount.vue";
import AppMainIcon from "../AppMainIcon.vue";
import AppPrice from "../AppPrice.vue";
import AppTextButton from "../AppTextButton.vue";
import Tooltip from "../Tooltip.vue";
import type { OpenedChest } from "./EventMarketBox.vue";

const props = defineProps({
	chestType: { type: String },
	multiplier: { type: Number, required: true },
	isKey: { type: Boolean },
	isFree: { type: Boolean },
	chests: { type: Array<OpenedChest[]>, required: true },
	onClaim: {
		type: Function,
		required: false,
	},
});

const emit = defineEmits(["closePopup"]);

const eventStore = useEventStore();
const { attributes, grandPrizeState } = storeToRefs(eventStore);

const userStore = useUserStore();
const { daysAfterRegistration } = storeToRefs(userStore);

const resourcesStore = useResourcesStore();
const { decrease } = resourcesStore;

interface MarketChest {
	type: MarketDropRateType;
	reward: GrandPrizeInGameReward | null;
}

const mutatedChests = computed((): MarketChest[][] => {
	const updatedData = props.chests.map((group: OpenedChest[]) =>
		group.map((item: OpenedChest): MarketChest => {
			return {
				...item,
				reward: toGrandPrizeInGameReward(
					item.reward,
					useResourcesStore(),
				),
			};
		}),
	);

	return updatedData;
});

const claimRewards = async (): Promise<void> => {
	if (props.onClaim) props.onClaim(); // IMPORTANT: Emit claim event first for queued popup
	emit("closePopup"); // Close market popup and activate queued event popup with its tab

	let nextGPState: {
		gemsEarnedToday?: number;
		chestKeysEarnedToday?: number;
		lastDailyFreePickedDaysAfterReg?: number;
		chestKeys?: number;
	} = {
		gemsEarnedToday: grandPrizeState.value?.gemsEarnedToday,
		chestKeysEarnedToday: grandPrizeState.value?.chestKeysEarnedToday,
	};

	const allRewards = mutatedChests.value.flatMap(
		(group: MarketChest[]): (GrandPrizeInGameReward | null)[] =>
			group.map(
				(item: MarketChest): GrandPrizeInGameReward | null =>
					item.reward,
			),
	);
	allRewards.forEach((reward: GrandPrizeInGameReward | null): void => {
		if (reward && reward.type === "currency") {
			if (reward.currency === "chestKey") {
				nextGPState = {
					...nextGPState,
					chestKeys:
						(grandPrizeState?.value?.chestKeys ?? 0) +
						reward.amount,
				};
				eventStore.mergeGrandPrizeState(nextGPState);
			} else if (!applyMilestoneCurrency(reward, "event market reward")) {
				return;
			}
		}

		if (reward && reward.type === "item") {
			if (!applyMilestoneBuilding(reward, "event market reward")) {
				return;
			}
		}
	});

	// free claim
	if (grandPrizeState.value && props.isFree) {
		nextGPState = {
			...nextGPState,
			lastDailyFreePickedDaysAfterReg: floorNumber(
				daysAfterRegistration.value,
			),
		};
		eventStore.mergeGrandPrizeState(nextGPState);
	}

	// key claim
	if (grandPrizeState.value && props.isKey) {
		nextGPState = {
			...nextGPState,
			chestKeys:
				(grandPrizeState.value.chestKeys ?? 0) - props.multiplier,
		};
		eventStore.mergeGrandPrizeState(nextGPState);
	}

	// ak ani jedno, odratanie eventia
	if (!props.isFree && !props.isKey) {
		const tier = attributes.value.grand_prizes?.tiers.find(
			(tier: EventGrandPrizeTierConfig): boolean =>
				tier.id === props.chestType,
		);
		decrease(
			ResourceType.EVENTIUM,
			(tier?.costEventium ?? 0) * props.multiplier,
			`event market chest - ${props.multiplier}x - ${props.chestType}`,
		);
	}

	// save event store
	if (nextGPState) {
		const ok = await saveGrandPrizeState({
			eventGrandPrizeState: nextGPState,
		});
		if (!ok) {
			useEmitter().emit(EmitterEvents.PhaserTextMessage, {
				text: translate("ui.connection_error_info"),
				type: TileToastType.WARNING,
				placement: ToastPlacement.TOP_CENTER,
			});
		}
	}
};

const buildingTooltip = (reward: GrandPrizeInGameRewardItem | null): string => {
	if (!reward) return "";

	if (reward.frameName === FrameName.TOYMAKERS_WORKSHOP) {
		return replacePlaceholder(
			replacePlaceholder(
				replacePlaceholder(
					translate(`${FrameName.TOYMAKERS_WORKSHOP}.tooltip`),
					"{number1}",
					500,
				),
				"{number2}",
				100,
			),
			"{number3}",
			100,
		);
	}
	if (
		[
			FrameName.RABBIT_FARM,
			FrameName.RAFT,
			FrameName.BOTTLETRAP,
			FrameName.FORTRESS,
		].includes(reward.frameName)
	) {
		return translate(`${reward.frameName}.description`);
	}
	return translate(`${reward.frameName}.name`);
};
</script>

<style lang="scss" scoped>
.event-chest-opened {
	position: relative;
	border-radius: 0.15rem;
	padding: 0.125rem;
	width: 100%;
	@include flex-col-start;
	min-height: 33em;

	&__header {
		position: relative;
		z-index: 1;
		width: 100%;
		height: 11.625rem;
		&-bg {
			display: flex;
			position: absolute;
			top: 0;
			left: 0;
			flex-direction: column;
			justify-content: center;
			align-items: center;
			width: 100%;
			height: 100%;
			overflow: hidden;

			&--market-chest {
				overflow-x: clip;
				overflow-y: visible;

				.market-chest-header-bg {
					margin-top: -2.813rem;
				}
			}
		}

		.market-chest-header {
			&-character {
				position: absolute;
				bottom: 0;
				left: 50%;
				transform: translateX(-50%);
			}

			&-heading {
				position: absolute;
				bottom: -1.75rem;
				left: 50%;
				transform: translateX(-50%);

				.market-chest-header-ribbon {
					// dynamically sets the size for the parent
					position: relative;
					filter: drop-shadow(0 0.5rem 0.75rem rgba(0, 0, 0, 0.8));
				}

				.heading-wrapper {
					position: absolute;
					top: calc(50% - 0.313rem);
					left: 50%;
					transform: translate(-50%, -50%);
					z-index: 1;
					width: 90%;

					:deep(.text-container) {
						.market-chest-header-ribbon-text {
							color: #f4d278;
							font-weight: 800;
							text-align: center;
							text-shadow: 0.044rem 0.044rem 0.063rem
								rgba(0, 0, 0, 0.6);
							text-transform: uppercase;
						}
					}
				}
			}
		}
	}
	&__content {
		position: relative;
		flex: 1 1 0;
		z-index: 0;
		width: 100%;
		@include flex-col-center;
		justify-content: space-between;
		background: linear-gradient(0deg, #ab8849, #f2dcb5);
		&::before {
			position: absolute;
			top: 0;
			left: 0;
			opacity: 0.6;
			z-index: 0;
			// NOTE: Color-burn blending is better for dark colors
			mix-blend-mode: color-burn;
			background: repeat center;
			@include background(
				url($path-images + "backgrounds/textures/marble-texture-bg.avif"),
				40%,
				center,
				repeat
			);
			width: 100%;
			height: 100%;
			content: "";
		}
	}
	&-rewards {
		padding: 1.8rem 0.5rem 0 0.5rem;
		width: 100%;
		height: 100%;
		&.more-row {
			@include flex-col-center;
		}
		&.one-row {
			@include flex-row-center;
			gap: 1.5rem;
			.event-chest-opened-reward {
				@include flex-col-center;
				gap: 0.25rem;
				z-index: 1;
				&-amount {
					@include flex-row-center;
					padding-bottom: 0.25rem;
					color: #fff3dd;
					font-weight: bold;
					font-size: 1.5rem;
					text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
				}
			}
		}
		&-chest {
			@include flex-row-center;
			justify-content: space-between;
			padding: 0.375rem 0;
			width: 100%;
			&:not(:last-child) {
				border-bottom: 0.063rem solid #f0d386;
			}
		}
		&-title {
			color: #372615;
			font-weight: bold;
			font-size: 0.9rem;
		}
		&-items {
			@include flex-row-center;
			z-index: 1;
			:deep(.price) {
				height: 100%;
			}
		}
	}
	&-footer {
		background-color: #27282b;
		@include flex-row-center;
		border-top: 0.063rem solid #f0d386;
		padding: 0.813rem;
		width: 100%;
	}
}
</style>
